import React, {FunctionComponent} from "react";
import {Formik} from "formik";
import {validator} from "@reside/forms";
import {CreateStaffUserDto, UserDto} from "../../../utils/types/user";
import {ThinScrollbar} from "@reside/ui";
import {useSelector} from "react-redux";
import {AppState, select} from "../../../store";

import {useOrganizationsQuery} from "../hooks/useOrganizationsQuery";
import {BackdropSpinner} from "../../../atoms/spinner";
import {normalizeValues, getValidationRules} from "./helpers";
import {SaveBeforeLeavePrompt} from "../../../atoms/save-before-leave-prompt/SaveBeforeLeavePrompt";
import {UserFormBodyRenderer} from "./UserFormBody";

type Props = Readonly<{
  initialValues: CreateStaffUserDto;
  onSubmit: (values: CreateStaffUserDto) => void;
  userFormHeader: React.ReactNode;
  user?: UserDto;
  deleteUserButton?: React.ReactNode;
  resetPasswordButton?: React.ReactNode;
  resendActivationEmailButton?: React.ReactNode;
}>;

export const UserFormik: FunctionComponent<Props> = ({
  initialValues,
  onSubmit,
  userFormHeader,
  user: userProp,
  deleteUserButton = null,
  resetPasswordButton = null,
  resendActivationEmailButton = null,
}) => {
  const {data: organizations = [], isFetching: isFetchingOrganizations} =
    useOrganizationsQuery();

  const adminSessionUser = useSelector((state: AppState) =>
    select.adminSession.user(state),
  );
  const user = userProp ?? adminSessionUser;
  const userOrganizationHasSsoConfig =
    user?.organization?.hasSsoConfig?.ssoIsEnabled === true;

  const validate = (values: CreateStaffUserDto) =>
    validator.validateAll(values, getValidationRules(values, organizations));

  return (
    <Formik<CreateStaffUserDto>
      enableReinitialize
      initialValues={initialValues}
      onSubmit={values => onSubmit(normalizeValues(values))}
      validate={validate}
    >
      {() => (
        <BackdropSpinner active={isFetchingOrganizations}>
          <SaveBeforeLeavePrompt />
          {userFormHeader}
          <ThinScrollbar>
            <UserFormBodyRenderer
              user={user}
              userOrganizationHasSsoConfig={userOrganizationHasSsoConfig}
              deleteUserButton={deleteUserButton}
              resetPasswordButton={resetPasswordButton}
              resendActivationEmailButton={resendActivationEmailButton}
              organizations={organizations}
            />
          </ThinScrollbar>
        </BackdropSpinner>
      )}
    </Formik>
  );
};
